'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function ElectricityCalculator() {
  const t = useTranslations("tools.electricityCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    appliance: '',
    power: 1000,
    powerUnit: 'W',
    capacity: 100,
    usage: 24,
    usageUnit: 'hpd',
    price: 0.15
  });

  const [results, setResults] = useState<any>(null);

  // Screen size detection
  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  // Responsive style helper
  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  // Appliance presets
  const appliancePresets = [
    { value: '', label: t('applianceCustom'), group: '' },
    { value: '5000:W:24:100', label: t('applianceAcHvac'), group: t('applianceGroupHouse') },
    { value: '1500:W:8:100', label: t('applianceAcWindow'), group: t('applianceGroupHouse') },
    { value: '5000:W:24:50', label: t('applianceHeaterHome'), group: t('applianceGroupHouse') },
    { value: '1500:W:8:100', label: t('applianceHeaterPortable'), group: t('applianceGroupHouse') },
    { value: '200:W:8:100', label: t('applianceHumidifier'), group: t('applianceGroupHouse') },
    { value: '300:W:8:100', label: t('applianceDehumidifier'), group: t('applianceGroupHouse') },
    { value: '25:W:8:50', label: t('applianceFan'), group: t('applianceGroupHouse') },
    { value: '7:W:8:100', label: t('applianceLightLed'), group: t('applianceGroupHouse') },
    { value: '60:W:8:100', label: t('applianceLightIncandescent'), group: t('applianceGroupHouse') },
    { value: '4000:W:1.5:100', label: t('applianceWaterHeater'), group: t('applianceGroupHouse') },
    { value: '750:W:24:20', label: t('applianceRefrigerator'), group: t('applianceGroupKitchen') },
    { value: '2400:W:1:50', label: t('applianceOven'), group: t('applianceGroupKitchen') },
    { value: '5000:W:1:30', label: t('applianceCooktop'), group: t('applianceGroupKitchen') },
    { value: '1000:W:0.17:75', label: t('applianceMicrowave'), group: t('applianceGroupKitchen') },
    { value: '1500:W:1:60', label: t('applianceDishwasher'), group: t('applianceGroupKitchen') },
    { value: '1000:W:0.05:30', label: t('applianceCoffeeMaker'), group: t('applianceGroupKitchen') },
    { value: '1000:W:0.25:100', label: t('applianceToaster'), group: t('applianceGroupKitchen') },
    { value: '1000:W:0.5:100', label: t('applianceKettle'), group: t('applianceGroupKitchen') },
    { value: '1000:W:0.75:50', label: t('applianceCooker'), group: t('applianceGroupKitchen') },
    { value: '10000:W:1.5:100', label: t('applianceEvCharger'), group: t('applianceGroupOther') },
    { value: '100:W:2:80', label: t('applianceTelevision'), group: t('applianceGroupOther') },
    { value: '800:W:0.29:80', label: t('applianceWasher'), group: t('applianceGroupOther') },
    { value: '3000:W:0.29:50', label: t('applianceDryer'), group: t('applianceGroupOther') },
    { value: '1000:W:0.29:50', label: t('applianceIron'), group: t('applianceGroupOther') },
    { value: '1500:W:0.17:50', label: t('applianceHairDryer'), group: t('applianceGroupOther') },
    { value: '150:W:10:30', label: t('applianceDesktop'), group: t('applianceGroupOther') },
    { value: '60:W:10:30', label: t('applianceLaptop'), group: t('applianceGroupOther') },
    { value: '15:W:3:50', label: t('appliancePhoneCharger'), group: t('applianceGroupOther') },
    { value: '1119:W:10:50', label: t('applianceWaterPump'), group: t('applianceGroupOther') },
  ];

  // Group appliances
  const groupedAppliances = appliancePresets.reduce((acc: any, item) => {
    if (!item.group) {
      acc[''] = acc[''] || [];
      acc[''].push(item);
    } else {
      acc[item.group] = acc[item.group] || [];
      acc[item.group].push(item);
    }
    return acc;
  }, {});

  // Handle appliance selection
  const handleApplianceChange = (value: string) => {
    setInputs({ ...inputs, appliance: value });
    if (value) {
      const [power, powerUnit, usage, capacity] = value.split(':');
      setInputs({
        ...inputs,
        appliance: value,
        power: parseFloat(power),
        powerUnit,
        usage: parseFloat(usage),
        capacity: parseFloat(capacity)
      });
    }
  };

  // Calculate electricity
  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    let powerWatts = inputs.power;
    if (inputs.powerUnit === 'kW') {
      powerWatts *= 1000;
    }

    let usageHoursPerDay = inputs.usage;
    switch(inputs.usageUnit) {
      case 'mpd':
        usageHoursPerDay /= 60;
        break;
      case 'hpw':
        usageHoursPerDay /= 7;
        break;
      case 'hpm':
        usageHoursPerDay /= 30;
        break;
    }

    const actualPowerWatts = powerWatts * (inputs.capacity / 100);
    const dailyKwh = (actualPowerWatts * usageHoursPerDay) / 1000;
    const weeklyKwh = dailyKwh * 7;
    const monthlyKwh = dailyKwh * 30;
    const yearlyKwh = dailyKwh * 365;

    const dailyCost = dailyKwh * inputs.price;
    const weeklyCost = weeklyKwh * inputs.price;
    const monthlyCost = monthlyKwh * inputs.price;
    const yearlyCost = yearlyKwh * inputs.price;

    // Generate tips
    const tips = [];
    if (yearlyCost > 100) {
      tips.push(t('tipHighCost'));
    }
    if (inputs.capacity === 100 && usageHoursPerDay > 12) {
      tips.push(t('tipReduceUsage'));
    }
    if (powerWatts > 2000) {
      tips.push(t('tipOffPeak'));
    }
    if (tips.length === 0) {
      tips.push(t('tipMonitor'));
      tips.push(t('tipTimer'));
      tips.push(t('tipUnplug'));
    }

    setResults({
      dailyKwh,
      weeklyKwh,
      monthlyKwh,
      yearlyKwh,
      dailyCost,
      weeklyCost,
      monthlyCost,
      yearlyCost,
      tips
    });
  };

  // Styles
  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      border: '1px solid rgba(37, 99, 235, 0.1)',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: '#1e40af', marginBottom: '0.5rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const formStyle = {
    background: 'white',
    borderRadius: '1rem',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    ...getResponsiveStyle(
      {},
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    )
  };

  const formGroupStyle = getResponsiveStyle(
    { marginBottom: '1.5rem' },
    {},
    {},
    {}
  );

  const labelStyle = getResponsiveStyle(
    { display: 'block', color: '#334155', fontWeight: 600, marginBottom: '0.5rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    border: '2px solid #e2e8f0',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
    boxSizing: 'border-box' as const,
  };

  const selectStyle = {
    ...inputStyle,
    cursor: 'pointer',
  };

  const inputWithUnitStyle = getResponsiveStyle(
    { display: 'flex', gap: '0.5rem' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const helpTextStyle = {
    fontSize: '0.85rem',
    color: '#64748b',
    marginTop: '0.25rem'
  };

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '1rem'
    },
    { padding: '1rem', fontSize: '1rem' },
    { padding: '1rem', fontSize: '1.1rem' },
    { padding: '1rem', fontSize: '1.1rem' }
  );

  const resultsStyle = {
    background: 'white',
    borderRadius: '1rem',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    marginTop: '2rem',
    ...getResponsiveStyle(
      {},
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    )
  };

  const resultGridStyle = getResponsiveStyle(
    {
      display: 'grid',
      gap: '1rem',
      marginBottom: '1.5rem'
    },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' },
    { gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem' }
  );

  const resultCardStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '0.75rem',
      borderLeft: '4px solid #2563eb'
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const resultLabelStyle = {
    display: 'block',
    color: '#64748b',
    fontSize: '0.9rem',
    fontWeight: 600,
    marginBottom: '0.5rem'
  };

  const resultValueStyle = getResponsiveStyle(
    { display: 'block', color: '#1e40af', fontWeight: 700 },
    { fontSize: '1.5rem' },
    { fontSize: '1.625rem' },
    { fontSize: '1.75rem' }
  );

  const resultUnitStyle = {
    fontSize: '1rem',
    color: '#64748b',
    fontWeight: 500
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse' as const,
    marginTop: '1.5rem'
  };

  const thStyle = {
    padding: '0.75rem',
    textAlign: 'left' as const,
    borderBottom: '1px solid #e2e8f0',
    background: '#f8fafc',
    color: '#475569',
    fontWeight: 600,
    fontSize: '0.9rem'
  };

  const tdStyle = {
    padding: '0.75rem',
    textAlign: 'left' as const,
    borderBottom: '1px solid #e2e8f0',
    color: '#334155'
  };

  const tipsSectionStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      borderRadius: '0.75rem',
      marginTop: '1.5rem',
      borderLeft: '4px solid #f59e0b'
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const glassCardStyle = {
    background: colors.glassBg,
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: '1rem',
    boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
    marginBottom: '2rem',
    ...getResponsiveStyle(
      {},
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    )
  };

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle = {
    marginLeft: '1.5rem',
    marginBottom: '1rem'
  };

  const listItemStyle = {
    marginBottom: '0.5rem',
    ...paragraphStyle
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>⚡ {t('toolTitle')}</h2>
            <p style={{ color: '#64748b', fontSize: '1rem' }}>{t('toolSubtitle')}</p>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="appliance">{t('applianceLabel')}</label>
              <select
                id="appliance"
                style={selectStyle}
                value={inputs.appliance}
                onChange={(e) => handleApplianceChange(e.target.value)}
              >
                {Object.keys(groupedAppliances).map((group) => {
                  if (group === '') {
                    return groupedAppliances[group].map((item: any) => (
                      <option key={item.value} value={item.value}>{item.label}</option>
                    ));
                  }
                  return (
                    <optgroup key={group} label={group}>
                      {groupedAppliances[group].map((item: any) => (
                        <option key={item.value} value={item.value}>{item.label}</option>
                      ))}
                    </optgroup>
                  );
                })}
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: screenSize === 'mobile' ? '1fr' : '1fr 1fr', gap: '1.5rem' }}>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="power">{t('powerLabel')}</label>
                <div style={inputWithUnitStyle}>
                  <input
                    type="number"
                    id="power"
                    style={{ ...inputStyle, flex: 1 }}
                    step="0.01"
                    min="0"
                    value={inputs.power}
                    onChange={(e) => setInputs({ ...inputs, power: parseFloat(e.target.value) || 0 })}
                    required
                  />
                  <select
                    style={{ ...selectStyle, flex: screenSize === 'mobile' ? 1 : '0 0 140px' }}
                    value={inputs.powerUnit}
                    onChange={(e) => setInputs({ ...inputs, powerUnit: e.target.value })}
                  >
                    <option value="W">{t('unitWatt')}</option>
                    <option value="kW">{t('unitKilowatt')}</option>
                  </select>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="capacity">{t('capacityLabel')}</label>
                <input
                  type="number"
                  id="capacity"
                  style={inputStyle}
                  min="1"
                  max="100"
                  value={inputs.capacity}
                  onChange={(e) => setInputs({ ...inputs, capacity: parseFloat(e.target.value) || 0 })}
                  required
                />
                <div style={helpTextStyle}>{t('capacityHelp')}</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: screenSize === 'mobile' ? '1fr' : '1fr 1fr', gap: '1.5rem' }}>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="usage">{t('usageLabel')}</label>
                <div style={inputWithUnitStyle}>
                  <input
                    type="number"
                    id="usage"
                    style={{ ...inputStyle, flex: 1 }}
                    step="0.01"
                    min="0"
                    value={inputs.usage}
                    onChange={(e) => setInputs({ ...inputs, usage: parseFloat(e.target.value) || 0 })}
                    required
                  />
                  <select
                    style={{ ...selectStyle, flex: screenSize === 'mobile' ? 1 : '0 0 140px' }}
                    value={inputs.usageUnit}
                    onChange={(e) => setInputs({ ...inputs, usageUnit: e.target.value })}
                  >
                    <option value="hpd">{t('unitHoursPerDay')}</option>
                    <option value="mpd">{t('unitMinutesPerDay')}</option>
                    <option value="hpw">{t('unitHoursPerWeek')}</option>
                    <option value="hpm">{t('unitHoursPerMonth')}</option>
                  </select>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="price">{t('priceLabel')}</label>
                <input
                  type="number"
                  id="price"
                  style={inputStyle}
                  step="0.001"
                  min="0"
                  value={inputs.price}
                  onChange={(e) => setInputs({ ...inputs, price: parseFloat(e.target.value) || 0 })}
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              style={calculateButtonStyle}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              {t('calculateButton')}
            </button>
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={{ ...h2Style, textAlign: 'center', marginBottom: '1.5rem' }}>
                📊 {t('resultsTitle')}
              </h3>

              <div style={resultGridStyle}>
                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t('dailyEnergy')}</span>
                  <span style={resultValueStyle}>
                    {results.dailyKwh.toFixed(2)} <span style={resultUnitStyle}>kWh</span>
                  </span>
                </div>

                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t('dailyCost')}</span>
                  <span style={resultValueStyle}>${results.dailyCost.toFixed(2)}</span>
                </div>

                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t('monthlyEnergy')}</span>
                  <span style={resultValueStyle}>
                    {results.monthlyKwh.toFixed(2)} <span style={resultUnitStyle}>kWh</span>
                  </span>
                </div>

                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t('monthlyCost')}</span>
                  <span style={resultValueStyle}>${results.monthlyCost.toFixed(2)}</span>
                </div>

                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t('yearlyEnergy')}</span>
                  <span style={resultValueStyle}>
                    {results.yearlyKwh.toFixed(2)} <span style={resultUnitStyle}>kWh</span>
                  </span>
                </div>

                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t('yearlyCost')}</span>
                  <span style={resultValueStyle}>${results.yearlyCost.toFixed(2)}</span>
                </div>
              </div>

              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>{t('tablePeriod')}</th>
                    <th style={thStyle}>{t('tableEnergy')}</th>
                    <th style={thStyle}>{t('tableCost')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={tdStyle}>{t('periodDaily')}</td>
                    <td style={tdStyle}>{results.dailyKwh.toFixed(2)} kWh</td>
                    <td style={tdStyle}>${results.dailyCost.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td style={tdStyle}>{t('periodWeekly')}</td>
                    <td style={tdStyle}>{results.weeklyKwh.toFixed(2)} kWh</td>
                    <td style={tdStyle}>${results.weeklyCost.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td style={tdStyle}>{t('periodMonthly')}</td>
                    <td style={tdStyle}>{results.monthlyKwh.toFixed(2)} kWh</td>
                    <td style={tdStyle}>${results.monthlyCost.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td style={{ ...tdStyle, borderBottom: 'none', fontWeight: 600, color: '#1e40af' }}>{t('periodYearly')}</td>
                    <td style={{ ...tdStyle, borderBottom: 'none', fontWeight: 600, color: '#1e40af' }}>{results.yearlyKwh.toFixed(2)} kWh</td>
                    <td style={{ ...tdStyle, borderBottom: 'none', fontWeight: 600, color: '#1e40af' }}>${results.yearlyCost.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>

              <div style={tipsSectionStyle}>
                <h4 style={{ color: '#92400e', fontSize: '1.1rem', marginBottom: '0.75rem' }}>
                  💡 {t('tipsTitle')}
                </h4>
                <ul style={{ margin: 0, paddingLeft: '1.5rem', color: '#78350f' }}>
                  {results.tips.map((tip: string, index: number) => (
                    <li key={index} style={{ marginBottom: '0.5rem' }}>{tip}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t('howToUseStep1')}</li>
          <li style={listItemStyle}>{t('howToUseStep2')}</li>
          <li style={listItemStyle}>{t('howToUseStep3')}</li>
          <li style={listItemStyle}>{t('howToUseStep4')}</li>
          <li style={listItemStyle}>{t('howToUseStep5')}</li>
        </ol>
      </section>

      {/* Key Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>
          {t('insightsStandardMethodTitle')}
        </h3>
        <p style={paragraphStyle}>{t('insightsStandardMethodText')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>
          {t('insightsSmartMeterTitle')}
        </h3>
        <p style={paragraphStyle}>{t('insightsSmartMeterText')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>
          {t('insightsUtilityToolsTitle')}
        </h3>
        <p style={paragraphStyle}>{t('insightsUtilityToolsText')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>
          {t('insightsProfessionalTitle')}
        </h3>
        <p style={paragraphStyle}>{t('insightsProfessionalText')}</p>
      </section>

      {/* Best Practices */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('bestPracticesTitle')}</h2>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>
          {t('bestPracticesCalculationTitle')}
        </h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('bestPracticesCalculation1')}</li>
          <li style={listItemStyle}>{t('bestPracticesCalculation2')}</li>
          <li style={listItemStyle}>{t('bestPracticesCalculation3')}</li>
        </ul>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>
          {t('bestPracticesAiTitle')}
        </h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('bestPracticesAi1')}</li>
          <li style={listItemStyle}>{t('bestPracticesAi2')}</li>
          <li style={listItemStyle}>{t('bestPracticesAi3')}</li>
        </ul>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>
          {t('bestPracticesAccuracyTitle')}
        </h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('bestPracticesAccuracy1')}</li>
          <li style={listItemStyle}>{t('bestPracticesAccuracy2')}</li>
          <li style={listItemStyle}>{t('bestPracticesAccuracy3')}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem' }}>
          {t('faq1Question')}
        </h3>
        <p style={paragraphStyle}>{t('faq1Answer')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem' }}>
          {t('faq2Question')}
        </h3>
        <p style={paragraphStyle}>{t('faq2Answer')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem' }}>
          {t('faq3Question')}
        </h3>
        <p style={paragraphStyle}>{t('faq3Answer')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem' }}>
          {t('faq4Question')}
        </h3>
        <p style={paragraphStyle}>{t('faq4Answer')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem' }}>
          {t('faq5Question')}
        </h3>
        <p style={paragraphStyle}>{t('faq5Answer')}</p>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('reference1')}</li>
          <li style={listItemStyle}>{t('reference2')}</li>
          <li style={listItemStyle}>{t('reference3')}</li>
          <li style={listItemStyle}>{t('reference4')}</li>
          <li style={listItemStyle}>{t('reference5')}</li>
        </ul>
      </section>
    </div>
  );
}